// ── InternalContractsPage ✦ — constitutional contract map ───────
//
// Admin-only, internal-only. The one discoverable surface where
// the platform's constitutional intent is collected for future
// contributors.
//
// IMPORTANT (2026-05-20 user direction):
//   This page is DOCUMENTATION-oriented, NOT observability-
//   oriented. Do NOT add:
//     • pass/fail percentages
//     • green/red status boards
//     • contributor attribution
//     • test timing or flaky-test metrics
//     • historical charts
//   It should feel like a constitution archive, not a DevOps
//   dashboard. Quiet, boring, clear, protective.
//
// Visual tone borrows from the Parent Dashboard palette — calm,
// generous whitespace, no neon, no charts. Read in 30 seconds.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, BookOpen, FileText, ArrowLeft, Lock, CheckCircle,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BG = '#0A0E1F';
const BG_SOFT = '#10162B';
const PANEL = 'rgba(255,255,255,0.035)';
const PANEL_BORDER = 'rgba(255,255,255,0.08)';
const TEXT = '#EEF1FA';
const TEXT_MUTED = '#9AA0BD';
const TEXT_SOFT = '#C5C9DE';
const BLUE = '#6DA8FF';
const VIOLET = '#9A8DFF';
const SAGE = '#86D4B5';
const FONT_STACK = "'Inter', 'Space Grotesk', system-ui, sans-serif";

// Map surface ids to accent colours. Order matches the backend.
const ACCENT = {
  parent_dashboard: BLUE,
  trust_language: VIOLET,
  emotional_flows: '#FFA7C7',
  feed_behaviour: '#FFD58A',
  adoption_metrics: SAGE,
};

const SurfaceCard = ({ surface }) => {
  const accent = ACCENT[surface.id] || BLUE;
  return (
    <section
      data-testid={`contract-surface-${surface.id}`}
      style={{
        padding: '28px 24px', borderRadius: 22, marginBottom: 18,
        background: PANEL, border: `1px solid ${PANEL_BORDER}`,
        backdropFilter: 'blur(6px)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', marginBottom: 10,
      }}>
        <p style={{
          margin: 0, fontFamily: FONT_STACK, fontWeight: 700, fontSize: 20,
          color: TEXT, letterSpacing: '-0.01em',
        }}>
          {surface.name}
        </p>
        <span
          data-testid={`contract-surface-${surface.id}-count`}
          style={{
            padding: '4px 11px', borderRadius: 999,
            background: `${accent}1A`, border: `1px solid ${accent}45`,
            fontSize: 11.5, fontWeight: 700, color: TEXT, letterSpacing: '0.02em',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <Lock size={11} weight="fill" color={accent} />
          {surface.guard_count} guards
        </span>
      </div>

      {/* Why this exists ✦ */}
      <div style={{
        padding: '12px 14px', borderRadius: 14, marginBottom: 18,
        background: 'rgba(255,255,255,0.025)',
        border: `1px solid ${accent}26`,
      }}>
        <p style={{
          margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: accent, opacity: 0.85,
        }}>
          ✦ Why this exists
        </p>
        <p style={{
          margin: '6px 0 0', color: TEXT_SOFT, fontSize: 13.5, lineHeight: 1.55,
        }}>
          {surface.why}
        </p>
      </div>

      {/* Principles */}
      <p style={{
        margin: '0 0 8px', fontSize: 10.5, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase', color: TEXT_MUTED,
      }}>
        Principles
      </p>
      <ul
        data-testid={`contract-surface-${surface.id}-principles`}
        style={{ margin: '0 0 18px', padding: '0 0 0 0', listStyle: 'none' }}
      >
        {surface.principles.map((p, i) => (
          <li
            key={i}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              color: TEXT_SOFT, fontSize: 13, lineHeight: 1.55, marginBottom: 6,
            }}
          >
            <span style={{
              marginTop: 7, width: 4, height: 4, borderRadius: '50%',
              background: accent, flexShrink: 0,
            }} />
            {p}
          </li>
        ))}
      </ul>

      {/* Invariants */}
      <p style={{
        margin: '0 0 8px', fontSize: 10.5, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase', color: TEXT_MUTED,
      }}>
        Protected invariants
      </p>
      <ul
        data-testid={`contract-surface-${surface.id}-invariants`}
        style={{ margin: '0 0 18px', padding: 0, listStyle: 'none' }}
      >
        {surface.invariants.map((inv, i) => (
          <li
            key={i}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              color: TEXT_SOFT, fontSize: 13, lineHeight: 1.55, marginBottom: 6,
            }}
          >
            <CheckCircle size={13} weight="fill" color={accent} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>{inv}</span>
          </li>
        ))}
      </ul>

      {/* Source pointers */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {[...surface.files, ...(surface.docs || [])].map((f) => (
          <span
            key={f}
            style={{
              padding: '4px 10px', borderRadius: 999, fontSize: 11,
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${PANEL_BORDER}`,
              color: TEXT_MUTED, fontFamily: 'ui-monospace, monospace',
            }}
          >
            <FileText size={10} weight="bold" style={{ marginRight: 6 }} />
            {f.replace('/app/', '')}
          </span>
        ))}
      </div>
    </section>
  );
};

const InternalContractsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/internal/contracts`);
        setData(r.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This surface is admin-only.');
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div
      data-testid="internal-contracts-page"
      style={{
        minHeight: '100vh', background: BG, color: TEXT,
        fontFamily: FONT_STACK, paddingBottom: 64,
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 18px' }}>
        <button
          onClick={() => navigate('/crew/admin')}
          data-testid="internal-contracts-back"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 999,
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${PANEL_BORDER}`,
            color: TEXT_MUTED, fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
            cursor: 'pointer', marginBottom: 18,
          }}
        >
          <ArrowLeft size={12} weight="bold" />
          Back to Admin
        </button>

        {/* Hero — quiet, archive feel */}
        <section
          data-testid="internal-contracts-hero"
          style={{
            padding: '28px 26px', borderRadius: 24, marginBottom: 22,
            background: `linear-gradient(160deg, ${BG_SOFT}, ${BG})`,
            border: `1px solid ${BLUE}33`,
            position: 'relative', overflow: 'hidden',
          }}
        >
          <div style={{
            position: 'absolute', top: -40, right: -40, width: 180, height: 180,
            borderRadius: '50%', background: VIOLET, opacity: 0.08, filter: 'blur(48px)',
          }} />
          <p style={{
            margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
            textTransform: 'uppercase', color: BLUE, opacity: 0.85,
          }}>
            EventDrop ✦ Internal
          </p>
          <p style={{
            margin: '8px 0 6px', fontFamily: FONT_STACK, fontWeight: 700,
            fontSize: 30, color: TEXT, letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            Constitutional Contract Map
          </p>
          <p style={{
            margin: '6px 0 14px', color: TEXT_SOFT, fontSize: 14, lineHeight: 1.6,
            maxWidth: 540,
          }}>
            The platform's institutional memory for product philosophy. Every
            invariant below is enforced in CI — change one and a test will
            fail. Read this before touching constitutional surfaces.
          </p>
          {data && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <span
                data-testid="internal-contracts-total"
                style={{
                  padding: '6px 14px', borderRadius: 999,
                  background: `${BLUE}1A`, border: `1px solid ${BLUE}55`,
                  fontSize: 12.5, fontWeight: 700, color: TEXT,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <ShieldCheck size={12} weight="fill" color={BLUE} />
                {data.total_guards} guards across {data.surfaces.length} surfaces
              </span>
              <span style={{
                padding: '6px 14px', borderRadius: 999,
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${PANEL_BORDER}`,
                fontSize: 12, color: TEXT_MUTED,
              }}>
                Documentation-oriented · never observability
              </span>
            </div>
          )}
        </section>

        {loading && (
          <p data-testid="internal-contracts-loading" style={{ color: TEXT_MUTED, fontSize: 13 }}>
            Loading ✦
          </p>
        )}

        {error && (
          <div
            data-testid="internal-contracts-error"
            style={{
              padding: '24px', borderRadius: 16,
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${PANEL_BORDER}`,
              color: TEXT_SOFT, fontSize: 13.5, lineHeight: 1.6,
            }}
          >
            <Lock size={16} weight="fill" color={BLUE} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {error}
          </div>
        )}

        {data && data.surfaces.map((s) => (
          <SurfaceCard key={s.id} surface={s} />
        ))}

        {data && (
          <div
            data-testid="internal-contracts-notes"
            style={{
              marginTop: 28, padding: '16px 18px', borderRadius: 14,
              background: 'rgba(255,255,255,0.025)',
              border: `1px solid ${PANEL_BORDER}`,
              display: 'flex', gap: 12, alignItems: 'flex-start',
            }}
          >
            <BookOpen size={16} weight="fill" color={SAGE} style={{ marginTop: 2, flexShrink: 0 }} />
            <p style={{
              margin: 0, color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.65,
            }}>
              {data.notes}
            </p>
          </div>
        )}

        <p
          data-testid="internal-contracts-footer"
          style={{
            margin: '24px 0 0', textAlign: 'center',
            color: TEXT_MUTED, fontSize: 11, lineHeight: 1.7,
          }}
        >
          Internal · admin-only · never surfaced to users · EventDrop ✦
        </p>

        {/* Calm link to source archives */}
        <p style={{
          margin: '12px 0 0', textAlign: 'center',
          color: TEXT_MUTED, fontSize: 11,
        }}>
          Source archive: <Link to="/crew/admin" style={{ color: TEXT_SOFT, textDecoration: 'none' }}>back to /crew/admin</Link>
        </p>
      </div>
    </div>
  );
};

export default InternalContractsPage;
